import React from 'react'
import { X } from 'lucide-react'
import {
  AnalyticsPeriod,
  AnalyticsSegment,
  DEFAULT_FILTERS,
  ProjectStatus,
  UnitStatus,
  useAnalyticsFilters,
} from '@/contexts/AnalyticsFiltersContext'
import { useAnalyticsFilterOptions } from '@/hooks/useAnalyticsFilterOptions'

/**
 * Strip of chips that always reflects the currently active filter context.
 *
 * Why: the filter bar collapses "More filters" by default, so once a user
 * drills into (say) Cascais + T2 + €700k–€1.5M they lose visual confirmation
 * of *which* filters are narrowing the dashboards. Every page was asking the
 * same question: "what's actually filtered right now?" — this component
 * answers it with one-click removal per dimension.
 */
export function ActiveFilterChips() {
  const { filters, setFilter, setFilters, reset } = useAnalyticsFilters()
  const { data: options } = useAnalyticsFilterOptions()

  const chips: ChipDescriptor[] = []

  // period — only show when user moved off the default
  if (filters.period !== DEFAULT_FILTERS.period) {
    chips.push({
      key: 'period',
      label: 'Period',
      value: PERIOD_LABELS[filters.period] ?? filters.period,
      onRemove: () => setFilter('period', DEFAULT_FILTERS.period),
    })
  }

  // segment
  if (filters.segment) {
    chips.push({
      key: 'segment',
      label: 'Segment',
      value: SEGMENT_LABELS[filters.segment] ?? filters.segment,
      onRemove: () => setFilter('segment', null),
    })
  }

  // include outliers toggle
  if (filters.include_outliers) {
    chips.push({
      key: 'include_outliers',
      label: 'Outliers',
      value: 'included',
      onRemove: () => setFilter('include_outliers', false),
    })
  }

  // string arrays
  for (const city of filters.cities) {
    chips.push({
      key: `city:${city}`,
      label: 'City',
      value: city,
      onRemove: () =>
        setFilter(
          'cities',
          filters.cities.filter((v) => v !== city),
        ),
    })
  }
  for (const district of filters.districts) {
    chips.push({
      key: `district:${district}`,
      label: 'District',
      value: district,
      onRemove: () =>
        setFilter(
          'districts',
          filters.districts.filter((v) => v !== district),
        ),
    })
  }
  for (const typology of filters.typologies) {
    chips.push({
      key: `typology:${typology}`,
      label: 'Typology',
      value: typology,
      onRemove: () =>
        setFilter(
          'typologies',
          filters.typologies.filter((v) => v !== typology),
        ),
    })
  }

  // price / m² ranges — coalesced into a single chip each so the strip stays readable
  if (filters.min_price != null || filters.max_price != null) {
    chips.push({
      key: 'price_range',
      label: 'Price',
      value: rangeLabel(filters.min_price, filters.max_price, '€'),
      onRemove: () => setFilters({ min_price: null, max_price: null }),
    })
  }
  if (filters.min_sqm != null || filters.max_sqm != null) {
    chips.push({
      key: 'sqm_range',
      label: 'Area',
      value: rangeLabel(filters.min_sqm, filters.max_sqm, '', 'm²'),
      onRemove: () => setFilters({ min_sqm: null, max_sqm: null }),
    })
  }

  // entity arrays — resolve IDs to names when lookup is available
  for (const id of filters.developer_ids) {
    const name = options?.developers.find((e) => e.id === id)?.name ?? `#${id}`
    chips.push({
      key: `developer:${id}`,
      label: 'Developer',
      value: name,
      onRemove: () =>
        setFilter(
          'developer_ids',
          filters.developer_ids.filter((v) => v !== id),
        ),
    })
  }
  for (const id of filters.broker_ids) {
    const name = options?.brokers.find((e) => e.id === id)?.name ?? `#${id}`
    chips.push({
      key: `broker:${id}`,
      label: 'Broker',
      value: name,
      onRemove: () =>
        setFilter(
          'broker_ids',
          filters.broker_ids.filter((v) => v !== id),
        ),
    })
  }
  for (const id of filters.architect_ids) {
    const name = options?.architects.find((e) => e.id === id)?.name ?? `#${id}`
    chips.push({
      key: `architect:${id}`,
      label: 'Architect',
      value: name,
      onRemove: () =>
        setFilter(
          'architect_ids',
          filters.architect_ids.filter((v) => v !== id),
        ),
    })
  }

  for (const status of filters.status) {
    chips.push({
      key: `status:${status}`,
      label: 'Unit status',
      value: STATUS_LABELS[status] ?? status,
      onRemove: () =>
        setFilter(
          'status',
          filters.status.filter((v) => v !== status),
        ),
    })
  }

  for (const status of filters.project_status) {
    chips.push({
      key: `project_status:${status}`,
      label: 'Project status',
      value: PROJECT_STATUS_LABELS[status] ?? status,
      onRemove: () =>
        setFilter(
          'project_status',
          filters.project_status.filter((v) => v !== status),
        ),
    })
  }

  if (filters.listing_date_from || filters.listing_date_to) {
    chips.push({
      key: 'listing_range',
      label: 'Listed',
      value: dateRangeLabel(filters.listing_date_from, filters.listing_date_to),
      onRemove: () =>
        setFilters({ listing_date_from: null, listing_date_to: null }),
    })
  }
  if (filters.sales_date_from || filters.sales_date_to) {
    chips.push({
      key: 'sales_range',
      label: 'Sold',
      value: dateRangeLabel(filters.sales_date_from, filters.sales_date_to),
      onRemove: () =>
        setFilters({ sales_date_from: null, sales_date_to: null }),
    })
  }

  if (chips.length === 0) return null

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-lg border border-secondary-200 bg-white px-3 py-2"
      aria-label="Active analytics filters"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-secondary-500">
        Active filters
      </span>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="group inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-200 hover:bg-primary-100"
          title={`Remove ${chip.label}: ${chip.value}`}
        >
          <span className="text-primary-500">{chip.label}:</span>
          <span className="font-semibold">{chip.value}</span>
          <X className="h-3 w-3 opacity-60 group-hover:opacity-100" aria-hidden="true" />
        </button>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={reset}
          className="ml-auto text-xs font-medium text-secondary-500 underline decoration-dotted hover:text-secondary-700"
        >
          Clear all
        </button>
      )}
    </div>
  )
}

interface ChipDescriptor {
  key: string
  label: string
  value: string
  onRemove: () => void
}

const PERIOD_LABELS: Record<AnalyticsPeriod, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  '6m': '6 months',
  '1y': '1 year',
  all: 'All time',
}

const SEGMENT_LABELS: Record<AnalyticsSegment, string> = {
  luxury: 'Luxury',
  mid: 'Mid',
  entry: 'Entry',
}

const STATUS_LABELS: Record<UnitStatus, string> = {
  available: 'Available',
  reserved: 'Reserved',
  sold: 'Sold',
  unknown: 'Unknown',
}

const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  active: 'Active',
  sold_out: 'Sold out',
  pending: 'Pending',
  cancelled: 'Cancelled',
  completed: 'Completed',
  inactive: 'Inactive',
}

function rangeLabel(
  min: number | null,
  max: number | null,
  prefix: string,
  suffix = '',
): string {
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
        ? `${(n / 1_000).toFixed(0)}k`
        : `${n}`
  if (min != null && max != null)
    return `${prefix}${fmt(min)}${suffix} – ${prefix}${fmt(max)}${suffix}`
  if (min != null) return `≥ ${prefix}${fmt(min)}${suffix}`
  if (max != null) return `≤ ${prefix}${fmt(max)}${suffix}`
  return ''
}

function dateRangeLabel(from: string | null, to: string | null): string {
  if (from && to) return `${from} → ${to}`
  if (from) return `from ${from}`
  if (to) return `to ${to}`
  return ''
}

